import { Injectable } from '@angular/core'
import {
  AccessLevel,
  DigitalSpecialist,
  DsUser,
  LLMTool,
  PaginatedResponse,
  ResourceData,
  Skill,
  SkillRelationships,
  ToolRelationships,
  UserInstance,
} from '@cwan-gpt-ui/models'
import {
  addSkill,
  addSpecialist,
  addTool,
  removeSkill,
  removeSpecialist,
  removeTool,
  RootState,
  selectDigitalSpecialistRecords,
  setAvailableTools,
  setDigitalSpecialists,
  setDsUsers,
  setKnowledge,
  setSelectedInstance,
  setSelectedSkill,
  setSelectedSpecialist,
  setSelectedTool,
  setSkills,
  setUpdatableDigitalSpecialists,
  setUpdatedSkill,
  setUpdatedSpecialist,
  setUpdatedTool,
  setUserInstances,
} from '@cwan-gpt-ui/state-management'
import { Store } from '@ngrx/store'
import { DigitalSpecialistApi } from './digital-specialist-api.service'

@Injectable({
  providedIn: 'root',
})
export class DigitalSpecialistService {
  private specialists: Record<string, DigitalSpecialist> = {}

  constructor(
    private readonly dsApi: DigitalSpecialistApi,
    private readonly store: Store<RootState>
  ) {
    //one thing about ngrx that grinds my gears is there's no direct access into redux state - only select with synchronous callback.
    // this makes code all loopy and hard to understand.  Sometimes I just want to get the state directly!
    this.store.select(selectDigitalSpecialistRecords).subscribe((dsMap) => {
      this.specialists = dsMap
    })
  }

  //----DIGITAL SPECIALIST ACTIONS----

  /**
   * Loads all specialists, now using pagination internally
   * @param accessLevel Access level for the request
   */
  async loadAllSpecialists(accessLevel = AccessLevel.READ, pageSize = 50) {
    const allSpecialists = await this.fetchAllPaginatedItems<DigitalSpecialist>(
      (pageSize, nextToken) =>
        this.dsApi.getSpecialistsWithPagination(
          accessLevel,
          pageSize,
          nextToken
        ),
      pageSize
    )

    this.store.dispatch(setDigitalSpecialists(allSpecialists))
  }

  async loadAllUpdatableDigitalSpecialists(accessLevel = AccessLevel.READ) {
    await this.loadAllUpdatableDigitalSpecialistsWithPagination(accessLevel, 50)
  }

  async loadAllUpdatableDigitalSpecialistsWithPagination(
    accessLevel = AccessLevel.READ,
    pageSize = 50
  ) {
    const allSpecialists = await this.fetchAllPaginatedItems<DigitalSpecialist>(
      (pageSize, nextToken) =>
        this.dsApi.getSpecialistsWithPagination(
          accessLevel,
          pageSize,
          nextToken
        ),
      pageSize
    )

    this.store.dispatch(setUpdatableDigitalSpecialists(allSpecialists))
  }

  /**
   * This loads the details - tool, skill, etc
   */
  async loadSpecialistDetails(username: string): Promise<DigitalSpecialist> {
    const ds: DigitalSpecialist = await this.dsApi.getSpecialist(username)
    this.store.dispatch(setUpdatedSpecialist(ds))
    return ds
  }

  /**
   * Provides direct access into the redux state
   */
  getCachedSpecialist(username: string): DigitalSpecialist | undefined {
    return this.specialists[username]
  }

  /**
   * Sets the selected digital specialists.
   * Loads it if it's not already been loaded
   * @param username
   */
  setSelectedSpecialist(username: string | undefined) {
    this.store.dispatch(setSelectedSpecialist(username))
  }

  async updateSpecialist(ds: DigitalSpecialist) {
    const updatedData = await this.dsApi.updateSpecialist(ds)

    //updates the instance in the store
    this.store.dispatch(setUpdatedSpecialist(updatedData))
  }

  async createSpecialist(ds: DigitalSpecialist) {
    const newDs = await this.dsApi.createSpecialist(ds)

    //adds the new instance to the store
    this.store.dispatch(addSpecialist(newDs))
  }

  async cloneSpecialist(username: string, newUsername: string) {
    const ds = await this.dsApi.cloneSpecialist(username, newUsername)

    //adds the new instance to the store
    this.store.dispatch(addSpecialist(ds))
  }

  async deleteSpecialist(username: string) {
    await this.dsApi.deleteSpecialist(username)
    //remove from store
    this.store.dispatch(removeSpecialist(username))
  }

  async loadKnowledge(username: string): Promise<string[]> {
    const files = await this.dsApi.getKnowledgeFiles(username)
    this.store.dispatch(setKnowledge(files))
    return files
  }

  async uploadKnowledgeFiles(username: string, files: FileList) {
    for (let i = 0; i < files.length; i++) {
      await this.dsApi.uploadKnowledgeFile(username, files[i])
    }
    this.loadKnowledge(username)
  }

  async deleteKnowledgeFiles(username: string, fileNames: string[]) {
    await this.dsApi.deleteKnowledgeFiles(username, fileNames)
    this.loadKnowledge(username)
  }

  //----TOOL ACTIONS----
  async loadAllTools(pageSize = 50) {
    const allTools = await this.fetchAllPaginatedItems<LLMTool>(
      (pageSize, nextToken) =>
        this.dsApi.getToolsWithPagination(pageSize, nextToken),
      pageSize
    )

    this.store.dispatch(setAvailableTools(allTools))
  }

  async loadTool(id: string): Promise<LLMTool> {
    const tool = await this.dsApi.getTool(id)
    this.store.dispatch(setUpdatedTool(tool))
    return tool
  }

  async getToolRelationships(id: string): Promise<ToolRelationships> {
    return await this.dsApi.getToolRelationships(id)
  }

  setSelectedTool(id: string | undefined) {
    this.store.dispatch(setSelectedTool(id))
  }

  async createTool(tool: LLMTool) {
    const newTool = await this.dsApi.createTool(tool)
    this.store.dispatch(addTool(newTool))
  }

  async updateTool(tool: LLMTool) {
    const updatedTool = await this.dsApi.updateTool(tool)
    this.store.dispatch(setUpdatedTool(updatedTool))
  }

  async deleteTool(toolId: string) {
    await this.dsApi.deleteTool(toolId)
    this.store.dispatch(removeTool(toolId))
    this.setSelectedTool(undefined)
  }

  //----SKILL ACTIONS----
  async loadAllSkills(pageSize = 50) {
    const allSkills = await this.fetchAllPaginatedItems<Skill>(
      (pageSize, nextToken) =>
        this.dsApi.getSkillsWithPagination(pageSize, nextToken),
      pageSize
    )

    this.store.dispatch(setSkills(allSkills))
  }

  async loadSkill(id: string): Promise<Skill> {
    const skill = await this.dsApi.getSkill(id)
    this.store.dispatch(setUpdatedSkill(skill))
    return skill
  }

  async getSkillRelationships(id: string): Promise<SkillRelationships> {
    return await this.dsApi.getSkillRelationships(id)
  }

  setSelectedSkill(id: string | undefined) {
    this.store.dispatch(setSelectedSkill(id))
  }

  async createSkill(skill: Skill) {
    const newSkill = await this.dsApi.createSkill(skill)
    this.store.dispatch(addSkill(newSkill))
  }

  async updateSkill(skill: Skill) {
    const updatedSkill = await this.dsApi.updateSkill(skill)
    this.store.dispatch(setUpdatedSkill(updatedSkill))
  }

  async deleteSkill(id: string) {
    await this.dsApi.deleteSkill(id)
    this.store.dispatch(removeSkill(id))
    this.setSelectedSkill(undefined)
  }

  // ----SESSION ACTIONS---- see digital-specialist-session.service.ts

  // ----USER INSTANCE ACTIONS----
  async loadAllUserInstances() {
    const instances: UserInstance[] = await this.dsApi.getUserInstances()
    this.store.dispatch(setUserInstances(instances))
  }

  async loadUserInstance(username: string) {
    const instance: UserInstance = await this.dsApi.getUserInstance(username)
    this.store.dispatch(setSelectedInstance(instance))
  }

  // ----DS SHARE INSTANCE ACTIONS----
  async loadDsUsers({ resourceId, sharedResouceType }: ResourceData) {
    if (!resourceId) return

    const dsUsers = await this.dsApi.getDsUsers(sharedResouceType, resourceId)
    this.store.dispatch(
      setDsUsers({ [sharedResouceType + resourceId]: dsUsers })
    )
  }

  async assignDsUsers(
    { resourceId, sharedResouceType }: ResourceData,
    data: DsUser[]
  ) {
    await this.dsApi.assignDsToUsers(sharedResouceType, resourceId, data)
  }

  async unassignDsUsers(
    { resourceId, sharedResouceType }: ResourceData,
    data: DsUser[]
  ) {
    await this.dsApi.unassignDsFromUsers(sharedResouceType, resourceId, data)
  }

  async getParameterTypes() {
    return await this.dsApi.getParameterTypes()
  }

  // ----Fetch Items with Pagination----
  private async fetchAllPaginatedItems<T>(
    fetchMethod: (
      pageSize: number,
      nextToken?: string
    ) => Promise<PaginatedResponse<T>>,
    pageSize = 50
  ): Promise<T[]> {
    const allItems: T[] = []
    let nextToken: string | undefined = undefined
    const maxPages = 100

    for (let page = 0; page < maxPages; page++) {
      try {
        const response: PaginatedResponse<T> = await fetchMethod(
          pageSize,
          nextToken
        )

        if (response?.items) {
          allItems.push(...response.items)
        }

        if (!response?.nextToken) {
          break
        }

        nextToken = response.nextToken
      } catch (error) {
        console.error('Error fetching paginated data:', error)
        break
      }
    }

    return allItems
  }
}
